import React, { useEffect, useRef, useState } from 'react'
import { Appointment } from '@myapp/contract'
import { getLeftPercent, getWidthPercent, INACTIVE_APPOINTMENT_STATUSES, getAppointmentColors, COMPLETED_TRAVEL_COLOR } from './timelineConstants'
import { SegmentedBlock, Segment } from './SegmentedBlock'
import { AppointmentInfoPopover } from './AppointmentInfoPopover'
import { shortLocation } from '../../../shared/helpers/address'
import { formatTimeHHmm } from '../../../shared/lib/timeline/dateHelpers'

const HOVER_DELAY_MS = 400
const CLOSE_DELAY_MS = 150

interface Props {
  appointment: Appointment
  onClick?: () => void
  isTransferTravel?: boolean
  isTransferDeparture?: boolean
  isEditing?: boolean
  laneIndex?: number
  laneCount?: number
}

export const AppointmentBlock = ({ appointment, onClick, isTransferTravel, isTransferDeparture, isEditing, laneIndex, laneCount }: Props) => {
  const isInactive = INACTIVE_APPOINTMENT_STATUSES.has(appointment.status)
  const isInProgress = !!appointment.actualStart && !appointment.actualEnd

  const workStart = new Date(appointment.actualStart ?? appointment.plannedStart)
  const travelDuration = appointment.actualTravelDuration ?? appointment.plannedTravelDuration
  const workDuration = appointment.actualWorkDuration ?? appointment.plannedWorkDuration
  const departureDuration = appointment.actualDepartureDuration ?? appointment.plannedDepartureDuration
  const anfahrtStart = new Date(workStart.getTime() - travelDuration * 60_000)
  const abfahrtStart = new Date(workStart.getTime() + workDuration * 60_000)

  const totalDuration = travelDuration + workDuration + departureDuration

  const left = getLeftPercent(anfahrtStart.toISOString())
  const width = getWidthPercent(totalDuration)

  const timeLabel = formatTimeHHmm(workStart.toISOString())
  const ext = appointment.workOrder?.external
  const location = shortLocation(ext)
  const gewerkeText = ext?.gewerkeText ?? undefined
  const auftragNr = ext ? `Nr. ${ext.auftragJahr}/${ext.auftragNr}` : undefined

  const { workColor, travelColor } = getAppointmentColors(appointment.status)

  const hasAttachments = !!appointment.attachments?.length

  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const openTimeoutRef = useRef<number | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (openTimeoutRef.current != null) window.clearTimeout(openTimeoutRef.current)
      if (closeTimeoutRef.current != null) window.clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  const cancelClose = () => {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimeoutRef.current = window.setTimeout(() => setAnchorRect(null), CLOSE_DELAY_MS)
  }

  const handleBlockEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ext) return
    cancelClose()
    const target = e.currentTarget
    if (openTimeoutRef.current != null) window.clearTimeout(openTimeoutRef.current)
    openTimeoutRef.current = window.setTimeout(() => {
      setAnchorRect(target.getBoundingClientRect())
    }, HOVER_DELAY_MS)
  }

  const handleBlockLeave = () => {
    if (openTimeoutRef.current != null) {
      window.clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    if (anchorRect) scheduleClose()
  }

  const handlePopoverEnter = () => {
    cancelClose()
  }

  const handlePopoverLeave = () => {
    scheduleClose()
  }

  const segments: Segment[] = [
    { duration: travelDuration, color: travelColor, topLabel: formatTimeHHmm(anfahrtStart.toISOString()), label: isTransferTravel ? 'Transfer Anfahrt' : 'Anfahrt', subLabel: `${travelDuration} min` },
    { duration: workDuration, color: workColor, topLabel: timeLabel, label: location ?? undefined, subLabel: gewerkeText, badge: auftragNr, hasAttachments, pulsing: isInProgress, statusPill: isInProgress ? 'Läuft' : undefined },
    { duration: departureDuration, color: appointment.status === 'COMPLETED' ? workColor : travelColor, topLabel: formatTimeHHmm(abfahrtStart.toISOString()), label: isTransferDeparture ? 'Transfer Abfahrt' : 'Abfahrt', subLabel: `${departureDuration} min` },
  ]

  return (
    <>
      <SegmentedBlock
        segments={segments}
        left={left}
        width={width}
        laneIndex={laneIndex}
        laneCount={laneCount}
        className={`border-r-2 border-white ${isEditing ? 'opacity-30 pointer-events-none' : ''} ${isInactive && !onClick ? 'cursor-default' : 'cursor-pointer'}`}
        style={{ marginRight: 2 }}
        onMouseDown={(e) => { e.stopPropagation(); if (onClick) onClick() }}
        onMouseEnter={handleBlockEnter}
        onMouseLeave={handleBlockLeave}
      />
      {anchorRect && ext && (
        <AppointmentInfoPopover
          anchorRect={anchorRect}
          external={ext}
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
        />
      )}
    </>
  )
}
